import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Phone, ArrowLeft, ArrowRight, Building2, User, Mail, Shield, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

const INDUSTRIES = [
  { value: "healthcare", label: "Healthcare / Medical" },
  { value: "legal", label: "Legal Services" },
  { value: "real_estate", label: "Real Estate" },
  { value: "restaurant", label: "Restaurant / Hospitality" },
  { value: "automotive", label: "Automotive" },
  { value: "financial", label: "Financial Services" },
  { value: "insurance", label: "Insurance" },
  { value: "retail", label: "Retail" },
  { value: "professional_services", label: "Professional Services" },
  { value: "technology", label: "Technology" },
  { value: "other", label: "Other" }
];

const CALL_VOLUMES = [
  { value: "0-100", label: "0 - 100 calls/month" },
  { value: "100-500", label: "100 - 500 calls/month" },
  { value: "500-1000", label: "500 - 1,000 calls/month" },
  { value: "1000-5000", label: "1,000 - 5,000 calls/month" },
  { value: "5000+", label: "5,000+ calls/month" }
];

export default function OnboardingQualification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    business_name: "",
    business_email: "",
    business_phone: "",
    industry: "",
    country: "CA",
    estimated_monthly_calls: ""
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("onboarding_session");
    const plan = localStorage.getItem("onboarding_plan");
    
    if (!token) {
      navigate("/pricing");
      return;
    }
    
    setSessionToken(token);
    setSelectedPlan(plan);
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) newErrors.first_name = "Required";
    if (!formData.last_name.trim()) newErrors.last_name = "Required";
    if (!formData.business_name.trim()) newErrors.business_name = "Required";
    
    if (!formData.business_email.trim()) {
      newErrors.business_email = "Required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.business_email)) {
      newErrors.business_email = "Invalid email format";
    }
    
    if (!formData.business_phone.trim()) {
      newErrors.business_phone = "Required";
    } else if (formData.business_phone.replace(/\D/g, "").length < 10) {
      newErrors.business_phone = "Invalid phone number";
    }
    
    if (!formData.industry) newErrors.industry = "Required";
    if (!formData.estimated_monthly_calls) newErrors.estimated_monthly_calls = "Required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/api/v1/onboarding/qualify/${sessionToken}`, formData);
      
      if (res.data.success) {
        // Store verification token (in production, this comes via email)
        localStorage.setItem("email_verification_token", res.data.verification_token);
        toast.success("Qualification submitted! Check your email to verify.");
        navigate("/onboarding/verify-email");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to submit qualification";
      toast.error(message);
    }
    
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-8 h-8 text-blue-500" />
            <span className="text-xl font-bold">Speakr</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold">1</div>
              <span className="hidden md:inline">Plan</span>
            </div>
            <div className="w-8 h-px bg-slate-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold">2</div>
              <span className="hidden md:inline">Details</span>
            </div>
            <div className="w-8 h-px bg-slate-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-400 text-sm">3</div>
              <span className="hidden md:inline text-slate-500">Payment</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-6 py-12 max-w-2xl">
        <Button 
          variant="ghost" 
          className="mb-6 text-slate-400 hover:text-white"
          onClick={() => navigate("/pricing")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Plans
        </Button>

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-3">
              <Building2 className="w-6 h-6 text-blue-500" />
              Tell us about your business
            </CardTitle>
            <CardDescription className="text-slate-400">
              This helps us customize your AI agent for your industry
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => handleChange("first_name", e.target.value)}
                      className={`pl-10 bg-slate-800 border-slate-700 ${errors.first_name ? "border-red-500" : ""}`}
                      placeholder="John"
                      data-testid="input-first-name"
                    />
                  </div>
                  {errors.first_name && <p className="text-red-400 text-sm">{errors.first_name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    className={`bg-slate-800 border-slate-700 ${errors.last_name ? "border-red-500" : ""}`}
                    placeholder="Doe"
                    data-testid="input-last-name"
                  />
                  {errors.last_name && <p className="text-red-400 text-sm">{errors.last_name}</p>}
                </div>
              </div>

              {/* Business Name */}
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="business_name"
                    value={formData.business_name}
                    onChange={(e) => handleChange("business_name", e.target.value)}
                    className={`pl-10 bg-slate-800 border-slate-700 ${errors.business_name ? "border-red-500" : ""}`}
                    placeholder="Acme Corporation"
                    data-testid="input-business-name"
                  />
                </div>
                {errors.business_name && <p className="text-red-400 text-sm">{errors.business_name}</p>}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="business_email">Business Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="business_email"
                    type="email"
                    value={formData.business_email}
                    onChange={(e) => handleChange("business_email", e.target.value)}
                    className={`pl-10 bg-slate-800 border-slate-700 ${errors.business_email ? "border-red-500" : ""}`}
                    placeholder="john@acmecorp.com"
                    data-testid="input-email"
                  />
                </div>
                {errors.business_email && <p className="text-red-400 text-sm">{errors.business_email}</p>}
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="business_phone">Business Phone *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="business_phone"
                    type="tel"
                    value={formData.business_phone}
                    onChange={(e) => handleChange("business_phone", e.target.value)}
                    className={`pl-10 bg-slate-800 border-slate-700 ${errors.business_phone ? "border-red-500" : ""}`}
                    placeholder="+1 (555) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
                {errors.business_phone && <p className="text-red-400 text-sm">{errors.business_phone}</p>}
              </div>

              {/* Industry & Country */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Industry *</Label>
                  <Select value={formData.industry} onValueChange={(v) => handleChange("industry", v)}>
                    <SelectTrigger className={`bg-slate-800 border-slate-700 ${errors.industry ? "border-red-500" : ""}`}>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {INDUSTRIES.map(ind => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.industry && <p className="text-red-400 text-sm">{errors.industry}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Country *</Label>
                  <Select value={formData.country} onValueChange={(v) => handleChange("country", v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="CA">🇨🇦 Canada</SelectItem>
                      <SelectItem value="US">🇺🇸 United States</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Call Volume */}
              <div className="space-y-2">
                <Label>Estimated Monthly Call Volume *</Label>
                <Select value={formData.estimated_monthly_calls} onValueChange={(v) => handleChange("estimated_monthly_calls", v)}>
                  <SelectTrigger className={`bg-slate-800 border-slate-700 ${errors.estimated_monthly_calls ? "border-red-500" : ""}`}>
                    <SelectValue placeholder="Select volume" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {CALL_VOLUMES.map(vol => (
                      <SelectItem key={vol.value} value={vol.value}>{vol.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.estimated_monthly_calls && <p className="text-red-400 text-sm">{errors.estimated_monthly_calls}</p>}
              </div>

              {/* Trust Note */}
              <div className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-lg">
                <Shield className="w-5 h-5 text-green-400 mt-0.5" />
                <div className="text-sm text-slate-400">
                  <p className="font-medium text-slate-300">Your data is secure</p>
                  <p>We use enterprise-grade encryption and never share your information.</p>
                </div>
              </div>

              {/* Submit */}
              <Button 
                type="submit" 
                className="w-full bg-blue-500 hover:bg-blue-600"
                disabled={loading}
                data-testid="submit-qualification"
              >
                {loading ? "Submitting..." : "Continue to Payment"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
